/* Ruby Rose — product carousel controller.
   Swiping is handled natively by CSS scroll-snap; this only adds the arrow
   controls, disabled states, and keyboard support. Written as a custom element
   so multiple instances on one page are independent. */

/* Wrapped so the file can be included more than once on a page. Each carousel
   section emits its own <script src>, and the homepage has three, so this runs
   three times. `class` is a top-level lexical declaration, so the second run
   threw "Identifier 'RubyCarousel' has already been declared" before reaching
   the guard at the bottom — two uncaught errors on every load. Scoping it here
   and returning early makes a repeat include a no-op. */
(function () {
  if (customElements.get("ruby-carousel")) return;

class RubyCarousel extends HTMLElement {
  connectedCallback() {
    this.init();

    // A non-deferred script upgrades the element before the parser has read its
    // children, leaving every query null. Retry once the document is parsed.
    if (!this.initialised) {
      document.addEventListener("DOMContentLoaded", () => this.init(), { once: true });
    }
  }

  init() {
    if (this.initialised) return;

    this.track = this.querySelector("[data-rr-track]");
    this.prevBtn = this.querySelector("[data-rr-prev]");
    this.nextBtn = this.querySelector("[data-rr-next]");

    if (!this.track) return;
    this.initialised = true;

    this.onScroll = this.throttle(() => this.syncControls(), 100);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.prevBtn?.addEventListener("click", () => this.step(-1));
    this.nextBtn?.addEventListener("click", () => this.step(1));

    this.track.addEventListener("keydown", (event) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.step(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.step(-1);
      }
    });

    // Re-measure when the breakpoint changes the number of visible cards.
    this.resizeObserver = new ResizeObserver(() => this.syncControls());
    this.resizeObserver.observe(this.track);

    this.syncControls();
  }

  disconnectedCallback() {
    this.track?.removeEventListener("scroll", this.onScroll);
    this.resizeObserver?.disconnect();
    clearTimeout(this.snapTimer);
  }

  /**
   * How far one arrow press travels: one card, or a whole screenful.
   *
   * Page mode advances by the number of WHOLE columns currently on screen, so
   * a two-row grid turns over its full set at once — four columns of two is
   * the eight products the mockup shows. Deriving the count from measurement
   * rather than the cards-per-view setting keeps it honest at every
   * breakpoint: on a phone showing one column it steps by one column, which
   * is still exactly one screenful.
   */
  get stride() {
    const card = this.track.querySelector("[data-rr-card]");
    if (!card) return this.track.clientWidth;

    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap) || 0;
    const one = card.getBoundingClientRect().width + gap;

    if (this.dataset.rrStep !== "page") return one;

    // clientWidth includes the track's own padding, and the track is inset by
    // the gutter, so the padding has to come off before counting columns.
    const padding =
      (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
    const inner = this.track.clientWidth - padding;

    // The trailing column needs no gap after it, hence the +gap. The +1 is a
    // sub-pixel tolerance and it matters: four 449.766px columns plus three
    // 15.936px gaps want 1846.87 in a box that measures 1846.7, so the exact
    // division gives 3.9996 and floor() silently pages by three columns
    // instead of four. A column cannot fit inside 1px, so this cannot
    // over-count.
    const columns = Math.max(1, Math.floor((inner + gap + 1) / one));
    return columns * one;
  }

  step(direction) {
    // scrollLeft is negative in RTL, so normalise by the direction of the axis.
    const rtl = getComputedStyle(this).direction === "rtl";
    const axis = rtl ? -1 : 1;
    const smooth = !this.prefersReducedMotion;
    const distance = this.stride * direction * axis;

    // Defensive: some browsers let `scroll-snap-type: mandatory` fight an
    // in-flight smooth scroll and yank the container back to its start point.
    // Snap is released for the duration of the animation and restored on
    // scrollend, so touch swiping still snaps normally.
    if (smooth) this.suspendSnap();

    this.track.scrollBy({ left: distance, behavior: smooth ? "smooth" : "auto" });

    if (smooth) this.resumeSnapWhenIdle();
  }

  suspendSnap() {
    this.track.style.scrollSnapType = "none";
  }

  resumeSnapWhenIdle() {
    clearTimeout(this.snapTimer);

    const restore = () => {
      this.track.style.scrollSnapType = "";
      this.syncControls();
    };

    if ("onscrollend" in window) {
      this.track.addEventListener("scrollend", restore, { once: true });
    }
    // Fallback: scrollend is not universal, and never fires if the scroll was
    // already at the boundary.
    this.snapTimer = setTimeout(restore, 700);
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  syncControls() {
    if (!this.prevBtn || !this.nextBtn) return;

    const { scrollWidth, clientWidth } = this.track;
    const position = Math.abs(this.track.scrollLeft);
    const maxScroll = scrollWidth - clientWidth;

    // 2px tolerance absorbs sub-pixel rounding at fractional card widths.
    const atStart = position <= 2;
    const atEnd = position >= maxScroll - 2;
    const overflows = maxScroll > 2;

    this.prevBtn.disabled = atStart || !overflows;
    this.nextBtn.disabled = atEnd || !overflows;
    this.toggleAttribute("data-rr-static", !overflows);
  }

  throttle(fn, wait) {
    let queued = false;
    return () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        setTimeout(() => {
          queued = false;
        }, wait);
        fn();
      });
    };
  }
}

  customElements.define("ruby-carousel", RubyCarousel);
})();
